import update from 'immutability-helper';
import _ from 'lodash';
import React, { useCallback } from 'react';

import {
  ChevronRight,
  ImportExport,
  Settings,
  Storage,
  Store
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  BaseWidget,
  ImportOrdersModal,
  PermissionWall,
  routeDirectory
} from 'modules';

import Bluebird from 'bluebird';
import { modalUtil } from 'iron-forge-react-modules';
import { useNavigate } from 'react-router-dom';
import * as AddStoreModal from './AddStoreModal/AddStoreModal';
import * as dataService from './dataService';



const BrandingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const ColorBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4
});

const ColorDot = styled(Box)<{ color: string; }>(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: 4,
  backgroundColor: color
}));



export const FC: React.FC = () => {
  const tuple = dataService.useQuery();
  const [testStoreConnection] = dataService.useTestStoreConnection();
  const navigate = useNavigate();
  const {
    modalType2IsOpen,
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'addStore',
    'importOrders'
  ]);

  const [storeId2IsConnected, setStoreId2IsConnected] = React.useState<Record<string, IStatus>>({});

  React.useEffect(() => {
    setStoreId2IsConnected(_.mapValues(_.keyBy(tuple.data.stores, s => s.id), () => 'unknown'));
  }, [tuple.data.stores]);



  function handleClickAddStore() {
    setModalIsOpen('addStore', true);
  }

  function handleCloseAddStoreModal() {
    setModalIsOpen('addStore', false);
  }

  const handleClickConnectionStatus = useCallback(
    async () => {
      await Bluebird.each(tuple.data.stores, async store => {
        const result = await testStoreConnection(store.id);
        setStoreId2IsConnected(oldValue => {
          return update(oldValue, {
            [store.id]: {
              $set: result.data?.testStoreConnection ? 'connected' : 'failed'
            }
          });
        });
      });
    },
    [
      testStoreConnection,
      tuple.data.stores
    ]);

  return (
    <>
      <BaseWidget.FC
        title="Stores"
        elAction={(
          <PermissionWall.FC
            requiresPermissions={['store_create']}>
            <Button
              variant="contained"
              onClick={handleClickAddStore}>
              Add Store
            </Button>
          </PermissionWall.FC>
        )}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Branding</TableCell>
              <TableCell>Prefix</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={handleClickConnectionStatus}>
                  Connection Status
                </Button>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {tuple.loading ? _.times(10).map(i => (
              <TableRow key={i}>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>

            )) : (

              tuple.data.stores.map(store => (
                <TableRow key={store.id}>
                  <TableCell>
                    <BrandingBox>
                      <Avatar
                        variant="rounded"
                        src={store.logoUrl}>
                        <Store />
                      </Avatar>

                      <ColorBox>
                        <ColorDot color={store.primaryColorHex} />
                        <ColorDot color={store.secondaryColorHex} />
                        <ColorDot color={store.buttonColorHex} />
                      </ColorBox>
                    </BrandingBox>
                  </TableCell>

                  <TableCell>
                    {store.orderPrefix}
                  </TableCell>

                  <TableCell>
                    {store.name}
                  </TableCell>

                  <TableCell>
                    {store.websiteUrl}
                  </TableCell>

                  <TableCell>
                    <StatusChip status={storeId2IsConnected[store.id] ?? 'unknown'} />
                  </TableCell>

                  <TableCell sx={{ textAlign: 'right' }}>

                    {store.type === 'customizableFormat' && (
                      <PermissionWall.FC
                        storeId={store.id}
                        requiresPermissions={['salesOrder_create']}>
                        <IconButton onClick={() => setModalIsOpen('importOrders', store.id)}>
                          <ImportExport />
                        </IconButton>
                      </PermissionWall.FC>
                    )}

                    <PermissionWall.FC
                      storeId={store.id}
                      requiresPermissions={['store_viewSettings']}>
                      <IconButton onClick={() => navigate(routeDirectory.admin.storeDetail.path(store.id, 'erpSettings'))}>
                        <Settings />
                      </IconButton>
                    </PermissionWall.FC>

                    <IconButton onClick={() => navigate(routeDirectory.admin.dataCache.path('valid', store.id))}>
                      <Storage />
                    </IconButton>

                    <IconButton onClick={() => navigate(routeDirectory.partner.storeDetail.path(store.id))}>
                      <ChevronRight />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )))}
          </TableBody>
        </Table>
      </BaseWidget.FC>

      {modalType2IsOpen.addStore && (
        <AddStoreModal.FC onClose={handleCloseAddStoreModal} />
      )}

      {modalType2IsOpen.importOrders && (
        <ImportOrdersModal.FC
          storeId={modalType2TargetId.importOrders}
          onAccept={() => setModalIsOpen('importOrders', false)}
          onCancel={() => setModalIsOpen('importOrders', false)} />
      )}
    </>
  );
};



type IStatus = 'unknown' | 'connected' | 'failed';



const StatusChip: React.FC<{
  status: IStatus;
}> = ({
  status
}: {
  status: IStatus;
}) => {
  switch (status) {
  case 'unknown':
    return <Chip color="default" label="Unknown" />;
  case 'failed':
    return <Chip color="error" label="Error" />;
  case 'connected':
    return <Chip color="success" label="Connected" />;
  }
};
